export const DEFAULT_BINDINGS = Object.freeze({
  forward: 'KeyW',
  back: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  jump: 'Space',
  sprint: 'ShiftLeft',
  crouch: 'ControlLeft',
  reload: 'KeyR',
  fireMode: 'KeyB',
  forge: 'Tab',
  pause: 'Escape',
  help: 'KeyH',
});

export const REBINDABLE_ACTIONS = Object.freeze([
  'forward', 'back', 'left', 'right', 'jump', 'sprint',
  'crouch', 'reload', 'fireMode', 'forge', 'pause', 'help',
]);

export const PAD_BUTTON = Object.freeze({
  confirm: 0,
  cancel: 1,
  secondary: 2,
  tertiary: 3,
  modifier: 4,
  weaponNext: 5,
  ads: 6,
  fire: 7,
  forge: 8,
  menu: 9,
  up: 12,
  down: 13,
  left: 14,
  right: 15,
});

const CODE_LABELS = Object.freeze({
  Space: 'SPACE', Escape: 'ESC', Tab: 'TAB', Enter: 'ENTER',
  ShiftLeft: 'L SHIFT', ShiftRight: 'R SHIFT',
  ControlLeft: 'L CTRL', ControlRight: 'R CTRL',
  AltLeft: 'L ALT', AltRight: 'R ALT',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  Backspace: 'BACKSPACE', Delete: 'DELETE',
});

const VALID_CODE = /^(?:Key[A-Z]|Digit[0-9]|F(?:[1-9]|1[0-2])|Space|Escape|Tab|Enter|Backspace|Delete|Home|End|Arrow(?:Up|Down|Left|Right)|Shift(?:Left|Right)|Control(?:Left|Right)|Alt(?:Left|Right))$/;

export function isBindableCode(code) {
  return typeof code === 'string' && VALID_CODE.test(code);
}

export function sanitizeBindings(value) {
  if (!value || typeof value !== 'object') return { ...DEFAULT_BINDINGS };
  const out = {};
  const used = new Set();
  for (const action of REBINDABLE_ACTIONS) {
    const requested = isBindableCode(value[action]) ? value[action] : DEFAULT_BINDINGS[action];
    const fallback = [DEFAULT_BINDINGS[action], ...Object.values(DEFAULT_BINDINGS)]
      .find((code) => !used.has(code));
    const code = !used.has(requested) ? requested : fallback;
    out[action] = code;
    used.add(code);
  }
  return out;
}

export function swapBinding(bindings, action, code) {
  const next = sanitizeBindings(bindings);
  if (!REBINDABLE_ACTIONS.includes(action) || !isBindableCode(code)) return next;
  const old = next[action];
  const conflict = REBINDABLE_ACTIONS.find((name) => name !== action && next[name] === code);
  next[action] = code;
  if (conflict) next[conflict] = old;
  return next;
}

export function bindingLabel(code) {
  if (CODE_LABELS[code]) return CODE_LABELS[code];
  if (/^Key[A-Z]$/.test(code || '')) return code.slice(3);
  if (/^Digit[0-9]$/.test(code || '')) return code.slice(5);
  return String(code || '—').toUpperCase();
}

export function radialDeadzone(x, y, deadzone = 0.18) {
  const dx = Number.isFinite(x) ? x : 0;
  const dy = Number.isFinite(y) ? y : 0;
  const mag = Math.min(1, Math.hypot(dx, dy));
  if (mag <= deadzone) return { x: 0, y: 0 };
  const scaled = (mag - deadzone) / (1 - deadzone);
  const k = scaled / (Math.hypot(dx, dy) || 1);
  return { x: dx * k, y: dy * k };
}

export function buttonValue(button) {
  if (typeof button === 'number') return button;
  if (!button) return 0;
  if (Number.isFinite(button.value)) return button.value;
  return button.pressed ? 1 : 0;
}

export function buttonDown(button, threshold = 0.5) {
  return buttonValue(button) >= threshold || button?.pressed === true;
}
